/* eslint-disable react/jsx-max-props-per-line, max-len, object-curly-spacing */
import React, {useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import styles from './serial-plotter.css';

const NEON_COLORS = [
    '#00f3ff', // Cyan
    '#ff00ff', // Magenta
    '#39ff14', // Neon Green
    '#ffeb3b', // Bright Yellow
    '#ff5722', // Neon Orange
    '#d500f9' // Purple
];

const SerialPlotter = ({messages}) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        
        // Handle High-DPI displays
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        const width = rect.width;
        const height = rect.height;

        // Clear canvas
        ctx.clearRect(0, 0, width, height);
        
        // Draw grid
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
        ctx.lineWidth = 1;
        for (let i = 0; i < width; i += 50) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, height);
            ctx.stroke();
        }
        for (let i = 0; i < height; i += 50) {
            ctx.beginPath();
            ctx.moveTo(0, i);
            ctx.lineTo(width, i);
            ctx.stroke();
        }

        if (!messages || messages.length === 0) return;

        // Parse numerical data from messages
        const data = [];
        messages.forEach(msg => {
            if (msg.type === 'incoming' && msg.content) {
                // Find all numbers (including decimals and negatives) separated by spaces or commas
                const matches = msg.content.match(/-?\d+(\.\d+)?/g);
                if (matches && matches.length > 0) {
                    data.push({values: matches.map(Number)});
                }
            }
        });

        // Limit to last 200 points so the graph scrolls and doesn't get squished
        const plotData = data.slice(-200);

        if (plotData.length === 0) return;

        // Find min and max for auto-scaling
        let min = Infinity;
        let max = -Infinity;
        let numSeries = 0;

        plotData.forEach(point => {
            if (point.values) {
                numSeries = Math.max(numSeries, point.values.length);
                point.values.forEach(val => {
                    if (val < min) min = val;
                    if (val > max) max = val;
                });
            }
        });

        if (numSeries === 0 || min === Infinity) return;
        
        // Add padding to auto-scale bounds
        const range = max - min === 0 ? 10 : max - min;
        const paddedMin = min - (range * 0.1);
        const paddedMax = max + (range * 0.1);
        const paddedRange = paddedMax - paddedMin;

        // Draw lines
        for (let s = 0; s < numSeries; s++) {
            ctx.beginPath();
            ctx.strokeStyle = NEON_COLORS[s % NEON_COLORS.length];
            ctx.lineWidth = 2;
            
            // Add a subtle glow for that kid-friendly neon look
            ctx.shadowBlur = 10;
            ctx.shadowColor = ctx.strokeStyle;

            for (let i = 0; i < plotData.length; i++) {
                const point = plotData[i];
                if (point.values && point.values.length > s) {
                    const val = point.values[s];
                    // Map value to canvas height
                    const x = (i / Math.max(plotData.length - 1, 1)) * width;
                    const y = height - (((val - paddedMin) / paddedRange) * height);
                    
                    if (i === 0) {
                        ctx.moveTo(x, y);
                    } else {
                        ctx.lineTo(x, y);
                    }
                }
            }
            ctx.stroke();
            ctx.shadowBlur = 0; // reset
        }
        
    }, [messages]);

    // Calculate number of series for the legend
    const data = [];
    messages.forEach(msg => {
        if (msg.type === 'incoming' && msg.content) {
            const matches = msg.content.match(/-?\d+(\.\d+)?/g);
            if (matches) data.push({values: matches.map(Number)});
        }
    });
    
    const numSeries = data.reduce((max, point) => (point.values ? Math.max(max, point.values.length) : max), 0);
    const legendItems = [];
    for (let i = 0; i < Math.min(numSeries, NEON_COLORS.length); i++) {
        legendItems.push(
            <div key={i} className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: NEON_COLORS[i], color: NEON_COLORS[i] }} />
                <span>{'Value '}{i + 1}</span>
            </div>
        );
    }

    return (
        <div className={styles.plotterContainer}>
            <canvas ref={canvasRef} className={styles.plotterCanvas} />
            {messages.length === 0 && (
                <div className={styles.emptyState}>{'Waiting for numerical data...'}</div>
            )}
            {legendItems.length > 0 && (
                <div className={styles.legend}>
                    {legendItems}
                </div>
            )}
        </div>
    );
};

SerialPlotter.propTypes = {
    messages: PropTypes.arrayOf(PropTypes.object)
};

export default SerialPlotter;
